import { AxiosInstance } from 'axios';
import {
  PDP_FRAMES_QUERY,
  QUERY_FOR_ORDER_HISTORY,
} from '../container/glasses/frames/brands/query';
import { FindProductWithAddressQuery } from '../container/plp/query';
import {
  IQueryFilter,
  getQueryFiltersByType,
  getQueryVariables,
} from './helper/pdp/frames/productUtils';
import { WebEnabled } from '../utils/commerceToolsUtils';
import { splunk_log_type, splunk_message } from '../common/splunk_constant';

export type ProductFilters = {
  isWebEnabled?: boolean;
  isProductAvailable?: boolean;
  productId: string;
};

export default class ProductService {
  static splunkLogger?: any;
  static getProducts(ctproxyBffClient: AxiosInstance, queryFilterObj: IQueryFilter): Promise<any> {
    const queryFilters = getQueryFiltersByType(queryFilterObj);
    return graphQLEcommerceClient(ctproxyBffClient, PDP_FRAMES_QUERY, queryFilters, { sort: ['price asc'] }, queryFilterObj.config);
  }


  /**
   * Fetches a product by its ID, with optional filters for web-enabled and availability status.
   * @param ctproxyBffClient - Axios instance for making API requests.
   * @param product - Product ID as a string or ProductFilters object.
   * @returns Promise resolving to the product data.
   */
  static getProductByID(
    ctproxyBffClient: AxiosInstance,
    product: string | ProductFilters
  ): Promise<any> {
    const productId = typeof product === 'string' ? product : product.productId;
    const queryParams = [
      { type: 'id', value: productId },
    ];

    // Add web-enabled filter if specified
    if (typeof product !== 'string' && product.isWebEnabled) {
      queryParams.push({
        type: 'variants.attributes.WEB_ENABLED.key',
        value: `${WebEnabled.VISIBLE_SELLABLE}", "${WebEnabled.VISIBLE_NOT_SELLABLE}`,
      });
    }

    // Add availability filter if specified
    if (typeof product !== 'string' && product.isProductAvailable) {
      queryParams.push({
        type: 'variants.attributes.IS_AVAILABLE',
        value: 'true',
      });
    }

    return graphQLEcommerceClient(ctproxyBffClient, PDP_FRAMES_QUERY, queryParams);
  }

  static getProductBySku(ctproxyBffClient: AxiosInstance, sku: string): Promise<any> {
    // TODO: Add parameters as needed
    const queryParams = [
      {
        type: 'variants.sku',
        value: sku,
      },
    ];
    return graphQLEcommerceClient(ctproxyBffClient, PDP_FRAMES_QUERY, queryParams);
  }

  static async getProductSearch(
    ctproxyBffClient: AxiosInstance,
    channelId: any,
    geoLocation: any,
    splunkLogger?: any
  ): Promise<any> {
    const queryFilters = [{ type: 'variants.prices.channel.id', value: channelId }];
    try {
      const response = await graphQLEcommerceClient(
        ctproxyBffClient,
        FindProductWithAddressQuery,
        queryFilters,
        { where: geoLocation },
      );
      splunkLogger?.({
        logType: splunk_log_type.INFO,
        data: splunk_message.SUCCESS.GET_PRODUCT_SEARCH_SUCCESS
      })
      return response;
    } catch (error) {
      splunkLogger?.({
        logType: splunk_log_type.ERROR,
        data: { ...splunk_message.ERROR.GET_PRODUCT_SEARCH_ERROR, error}
      })
      console.error('Error fetching product search data:', error);
      throw error;
    }
  }

  static getProductBySkuForOrderHistory(ctproxyBffClient: AxiosInstance, sku: string): Promise<any> {
    // TODO: Add parameters as needed
    const queryParams = [
      {
        type: 'variants.sku',
        value: sku,
      },
    ];
    return graphQLEcommerceClient(ctproxyBffClient, QUERY_FOR_ORDER_HISTORY, queryParams);
  }
}

export const graphQLEcommerceClient = async (
  ctproxyBffClient: AxiosInstance,
  query: any,
  queryParams: any,
  variables: any = { sort: ['price asc'] },
  config?:any
) => {
  const queryVariables = config ? getQueryVariables(queryParams, config) : getQueryVariables(queryParams)
  const obj = {
    query,
    variables: {
      ...queryVariables,
      ...variables,
    },
  };
  return ctproxyBffClient?.post('/api/v1/ecommerce', obj);
};
